import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getQuoteDetail, requestFinalOffer } from '@/lib/quotationService';
import type { CustomerQuoteDetailData } from '@/lib/quotationTypes';
import type { QuotationDocumentData } from '@/quotation/document/types';
import { mapPreliminaryQuoteToDocument } from '@/quotation/document/mappers/mapPreliminaryQuoteToDocument';
import QuotationHtmlDocument from '@/quotation/html/QuotationHtmlDocument';
import { useQuotationPdfDownload } from '@/quotation/download/useQuotationPdfDownload';

const statusChipMap: Record<string, string> = {
  priced: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  final_offer_requested: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
  superseded: 'bg-ink-50 text-ink-500 border-ink-200',
};

const statusLabelMap: Record<string, string> = {
  priced: '견적 완료',
  final_offer_requested: '본사 검토중',
  cancelled: '취소됨',
  superseded: '대체됨',
};

export default function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('quotation');
  const [quote, setQuote] = useState<CustomerQuoteDetailData | null>(null);
  const [documentData, setDocumentData] = useState<QuotationDocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [foLoading, setFoLoading] = useState(false);
  const [foError, setFoError] = useState<string | null>(null);
  const { downloading, failed, download } = useQuotationPdfDownload();

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // Single API fetch → one immutable snapshot. Both the HTML body and the
      // downloaded PDF are derived from this same object (no second request).
      const data = await getQuoteDetail(id);
      setQuote(data);
      setDocumentData(mapPreliminaryQuoteToDocument(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : '견적 상세 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleRequestFinalOffer = async () => {
    if (!id) return;
    setFoLoading(true);
    setFoError(null);
    try {
      const result = await requestFinalOffer(id);
      setQuote((prev) =>
        prev ? { ...prev, status: result.status, submittedAt: result.submittedAt } : prev,
      );
    } catch (err) {
      setFoError(err instanceof Error ? err.message : '최종 견적 요청 중 오류가 발생했습니다.');
    } finally {
      setFoLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <div className="w-8 h-8 border-2 border-ink-200 border-t-ink-500 rounded-full animate-spin" />
        <p className="text-sm text-ink-400">견적 정보를 불러오는 중...</p>
      </div>
    );
  }

  if (error || !quote || !documentData) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-3">
          <i className="ri-error-warning-line text-rose-500 text-xl" />
        </div>
        <p className="text-sm text-rose-600 font-medium">{error || '견적을 찾을 수 없습니다.'}</p>
        <button
          onClick={() => navigate('/my/quotes')}
          className="mt-3 px-4 py-2 text-xs font-bold text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          견적 목록으로
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* App shell header: back + identity + Download PDF */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <button
            onClick={() => navigate('/my/quotes')}
            className="inline-flex items-center gap-1 text-xs font-medium text-ink-500 hover:text-ink-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            견적 목록으로
          </button>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <h1 className="text-lg md:text-xl font-bold text-ink-700">{t('quotation.preliminary')}</h1>
            <span className="text-xs font-mono text-ink-400">{quote.quoteNumber}</span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${
                statusChipMap[quote.status] || 'bg-ink-50 text-ink-500 border-ink-200'
              }`}
            >
              {statusLabelMap[quote.status] || quote.status}
            </span>
          </div>
        </div>

        <button
          onClick={() => download(documentData)}
          disabled={downloading}
          className="px-4 py-2.5 text-xs font-bold text-white bg-ink-700 rounded-lg hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50 shrink-0"
        >
          {downloading ? (
            <i className="ri-loader-4-line animate-spin" />
          ) : (
            <i className="ri-download-2-line" />
          )}
          {downloading ? t('quotation.pdfGenerating') : t('quotation.pdfDownload')}
        </button>
      </div>

      {/* PDF generation error */}
      {failed && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          <i className="ri-information-line mt-0.5 shrink-0" />
          <span>{t('quotation.pdfError')}</span>
        </div>
      )}

      {/* Final Offer banner */}
      {quote.finalOfferAvailable && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <i className="ri-checkbox-circle-line text-emerald-600 text-lg" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-emerald-700">최종 견적서가 발행되었습니다.</p>
              <p className="text-xs text-emerald-600 mt-1 leading-relaxed">
                본사 검토가 완료되었습니다. 배송, 결제조건 및 최종 거래조건을 확인해주세요.
              </p>
              {quote.issuedFinalOfferId && (
                <button
                  onClick={() => navigate(`/my/final-offers/${quote.issuedFinalOfferId}`)}
                  className="mt-3 px-4 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-file-text-line" />
                  최종 견적서 보기
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* The actual HTML quotation — the customer-facing document */}
      <QuotationHtmlDocument data={documentData} />

      {/* Request Final Offer */}
      {quote.status === 'priced' && (
        <section className="bg-white border border-ink-100 rounded-xl p-4 md:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-ink-700">최종 견적 요청</p>
              <p className="text-[11px] text-ink-400 mt-0.5">
                견적 내용을 확인하셨다면 본사에 최종 견적 발행을 요청하세요.
              </p>
            </div>
            <button
              onClick={handleRequestFinalOffer}
              disabled={foLoading}
              className="px-5 py-2.5 text-xs font-bold text-white bg-warm-500 rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 disabled:opacity-50"
            >
              <i className="ri-send-plane-line" />
              {foLoading ? '요청 중...' : '최종 견적 요청'}
            </button>
          </div>
          {foError && (
            <div className="mt-3 flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-600">
              <i className="ri-error-warning-line mt-0.5 shrink-0" />
              <span>{foError}</span>
            </div>
          )}
        </section>
      )}
    </div>
  );
}